import { Handle, Position, useReactFlow } from '@xyflow/react'
import { Activity, Play, Square, RefreshCw, CheckCircle, XCircle } from 'lucide-react'
import { useState, useCallback, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/use-toast'
import styles from '@/styles/NodeWorkflow.module.css'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'

interface WorkflowStep {
  id: string
  name: string
  type: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped'
  progress: number
  startTime?: Date
  endTime?: Date
  error?: string
}

interface NodeWorkflowData {
  label: string
  informationClassification: string
  createdBy: string
  createdAt: string
  workflowName: string
  workflowDefinition: string
  triggerCondition: string
  autoExecute: boolean
  parallelExecution: boolean
  maxRetries: number
  retryDelay: number
  timeout: number
  statusMessage: string
  isMinimized: boolean
  isLocked: boolean
  isExecuting?: boolean
  executionResult?: any
  workflowSteps?: WorkflowStep[]
  currentStep?: number
  overallProgress?: number
  executionId?: string
}

interface NodeWorkflowProps {
  id: string
  data: NodeWorkflowData
  selected?: boolean
}

export default function NodeWorkflow({ id, data, selected }: NodeWorkflowProps) {
  const { updateNodeData } = useReactFlow()
  const { toast } = useToast()
  
  // ✅ Initialize with safe defaults (not from collaborative state)
  const [workflowName, setWorkflowName] = useState('')
  const [workflowDefinition, setWorkflowDefinition] = useState('')
  const [triggerCondition, setTriggerCondition] = useState('manual')
  const [autoExecute, setAutoExecute] = useState(false)
  const [parallelExecution, setParallelExecution] = useState(false)
  const [maxRetries, setMaxRetries] = useState(3)
  const [retryDelay, setRetryDelay] = useState(1000)
  const [timeout, setTimeout] = useState(300000)
  const [isExecuting, setIsExecuting] = useState(false)
  const [executionResult, setExecutionResult] = useState<any>(null)
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([])
  const [currentStep, setCurrentStep] = useState(0)
  const [overallProgress, setOverallProgress] = useState(0)
  const [executionId, setExecutionId] = useState<string | null>(null)

  // ✅ Sync from collaborative state (separate useEffects)
  useEffect(() => {
    setWorkflowName(data.workflowName || '')
  }, [data.workflowName])

  useEffect(() => {
    setWorkflowDefinition(data.workflowDefinition || '')
  }, [data.workflowDefinition])

  useEffect(() => {
    setTriggerCondition(data.triggerCondition || 'manual')
  }, [data.triggerCondition])

  useEffect(() => {
    setAutoExecute(data.autoExecute || false)
  }, [data.autoExecute])

  useEffect(() => {
    setParallelExecution(data.parallelExecution || false)
  }, [data.parallelExecution])

  useEffect(() => {
    setMaxRetries(data.maxRetries || 3)
  }, [data.maxRetries])

  useEffect(() => {
    setRetryDelay(data.retryDelay || 1000)
  }, [data.retryDelay])

  useEffect(() => {
    setTimeout(data.timeout || 300000)
  }, [data.timeout])

  useEffect(() => {
    setIsExecuting(data.isExecuting || false)
  }, [data.isExecuting])

  useEffect(() => {
    setExecutionResult(data.executionResult || null)
  }, [data.executionResult])

  useEffect(() => {
    setWorkflowSteps(data.workflowSteps || [])
  }, [data.workflowSteps])

  useEffect(() => {
    setCurrentStep(data.currentStep || 0)
  }, [data.currentStep])

  useEffect(() => {
    setOverallProgress(data.overallProgress || 0)
  }, [data.overallProgress])

  useEffect(() => {
    setExecutionId(data.executionId || null)
  }, [data.executionId])

  // ✅ Collaborative sync function (only call on user actions)
  const syncNodeData = useCallback((updates: Partial<NodeWorkflowData>) => {
    updateNodeData(id, { ...data, ...updates })
  }, [id, data, updateNodeData])

  // Execute workflow
  const executeWorkflow = useCallback(async () => {
    try {
      setIsExecuting(true)
      setOverallProgress(0)
      setCurrentStep(0)
      
      // Generate execution ID
      const newExecutionId = `workflow-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      setExecutionId(newExecutionId)
      
      // Initialize workflow steps from definition
      const steps: WorkflowStep[] = [
        { id: '1', name: 'Initialize', type: 'setup', status: 'running', progress: 0 },
        { id: '2', name: 'Process', type: 'main', status: 'pending', progress: 0 },
        { id: '3', name: 'Finalize', type: 'cleanup', status: 'pending', progress: 0 }
      ]
      
      setWorkflowSteps(steps)
      
      // Simulate workflow execution
      for (let i = 0; i < steps.length; i++) {
        setCurrentStep(i)
        const step = steps[i]
        step.status = 'running'
        step.startTime = new Date()
        
        // Simulate step progress
        for (let progress = 0; progress <= 100; progress += 20) {
          step.progress = progress
          setWorkflowSteps([...steps])
          setOverallProgress((i * 100 + progress) / steps.length)
          await new Promise(resolve => window.setTimeout(resolve, 100))
        }
        
        step.status = 'completed'
        step.endTime = new Date()
        step.progress = 100
        setWorkflowSteps([...steps])
      }
        setOverallProgress(100)
      const finalResult = { success: true, completedAt: new Date() }
      setExecutionResult(finalResult)
      
      // Sync final state to collaborative state
      syncNodeData({
        isExecuting: false,
        workflowSteps: steps,
        currentStep: steps.length - 1,
        overallProgress: 100,
        executionResult: finalResult,
        executionId: newExecutionId
      })
      
      toast({
        title: 'Workflow Executed',
        description: 'Workflow completed successfully'
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        // Update current step with error
      const updatedSteps = [...workflowSteps]
      if (updatedSteps[currentStep]) {
        updatedSteps[currentStep].status = 'failed'
        updatedSteps[currentStep].error = errorMessage
        setWorkflowSteps(updatedSteps)
        
        // Sync error state to collaborative state
        syncNodeData({
          isExecuting: false,
          workflowSteps: updatedSteps
        })
      }
      
      toast({
        title: 'Workflow Failed',
        description: errorMessage,
        variant: 'destructive'
      })    } finally {
      setIsExecuting(false)
      // Note: syncNodeData call is handled in success/error blocks above
    }
  }, [workflowDefinition, workflowSteps, currentStep, syncNodeData, toast])
  // Stop workflow execution
  const stopWorkflow = useCallback(() => {
    setIsExecuting(false)
    const updatedSteps = [...workflowSteps]
    if (updatedSteps[currentStep]) {
      updatedSteps[currentStep].status = 'failed'
      updatedSteps[currentStep].error = 'Execution stopped by user'
      setWorkflowSteps(updatedSteps)
    }
    
    // Sync stopped state to collaborative state
    syncNodeData({
      isExecuting: false,
      workflowSteps: updatedSteps
    })
    
    toast({
      title: 'Workflow Stopped',
      description: 'Workflow execution has been stopped'
    })
  }, [workflowSteps, currentStep, syncNodeData, toast])
  // Reset workflow
  const resetWorkflow = useCallback(() => {
    setIsExecuting(false)
    setExecutionResult(null)
    setWorkflowSteps([])
    setCurrentStep(0)
    setOverallProgress(0)
    setExecutionId(null)
    
    // Sync reset state to collaborative state
    syncNodeData({
      isExecuting: false,
      executionResult: null,
      workflowSteps: [],
      currentStep: 0,
      overallProgress: 0,
      executionId: null
    })
    
    toast({
      title: 'Workflow Reset',
      description: 'Workflow has been reset to initial state'
    })
  }, [syncNodeData, toast])

  // Get status icon for workflow step
  const getStepIcon = (status: WorkflowStep['status']) => {
    switch (status) {
      case 'running':
        return <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'skipped':
        return <Square className="h-4 w-4 text-gray-400" />
      default:
        return <Square className="h-4 w-4 text-gray-300" />
    }
  }

  return (    <NodeBaseContainer
      id={id}
      data={data}
      selected={selected}
      nodeTitle={data.label || 'Workflow'}
      isLocked={data.isLocked}
      isMinimized={data.isMinimized}
      className={`${styles.nodeWorkflow} bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 ${selected ? 'ring-2 ring-purple-400' : ''}`}
      resizable={true}
      minWidth={400}
      minHeight={350}    >
      <Handle
        type="target"
        position={Position.Top}
        id="trigger"
        className="w-3 h-3 bg-purple-400 border-2 border-white"
        isConnectable={true}
      />
      
      <div className={styles.nodeContent}>
        <div className={styles.nodeMainContent}>
          <div className={styles.contentStack}>
            <Card className={`${styles.workflowCard} w-full`}>
              <CardHeader className="pb-3" hasCardAction={false}>
          <CardTitle className="text-sm font-semibold text-purple-700 flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Workflow Execution
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Workflow Configuration */}
          <div className="space-y-3">
            <div>
              <Label htmlFor={`workflow-name-${id}`} className="text-xs font-medium">
                Workflow Name
              </Label>              <Input
                id={`workflow-name-${id}`}
                value={workflowName}
                onChange={(e) => {
                  const newValue = e.target.value
                  setWorkflowName(newValue)
                  syncNodeData({ workflowName: newValue })
                }}
                placeholder="Enter workflow name"
                className="h-8 text-xs"
              />
            </div>

            <div>
              <Label htmlFor={`workflow-definition-${id}`} className="text-xs font-medium">
                Workflow Definition
              </Label>              <Input
                id={`workflow-definition-${id}`}
                value={workflowDefinition}
                onChange={(e) => {
                  const newValue = e.target.value
                  setWorkflowDefinition(newValue)
                  syncNodeData({ workflowDefinition: newValue })
                }}
                placeholder="JSON or YAML workflow definition"
                className="h-8 text-xs"
              />
            </div>

            <div>
              <Label htmlFor={`trigger-condition-${id}`} className="text-xs font-medium">
                Trigger Condition
              </Label>
              <Select value={triggerCondition} onValueChange={(value) => {
                setTriggerCondition(value)
                syncNodeData({ triggerCondition: value })
              }}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select trigger" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="event">Event-based</SelectItem>
                  <SelectItem value="webhook">Webhook</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Execution Options */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor={`auto-execute-${id}`} className="text-xs font-medium">
                Auto Execute
              </Label>              <Switch
                id={`auto-execute-${id}`}
                checked={autoExecute}
                onCheckedChange={(checked) => {
                  setAutoExecute(checked)
                  syncNodeData({ autoExecute: checked })
                }}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor={`parallel-execution-${id}`} className="text-xs font-medium">
                Parallel Execution
              </Label>              <Switch
                id={`parallel-execution-${id}`}
                checked={parallelExecution}
                onCheckedChange={(checked) => {
                  setParallelExecution(checked)
                  syncNodeData({ parallelExecution: checked })
                }}
              />
            </div>            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor={`max-retries-${id}`} className="text-xs font-medium">
                  Max Retries
                </Label>                <Input
                  id={`max-retries-${id}`}
                  type="number"
                  value={maxRetries}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value) || 0
                    setMaxRetries(newValue)
                    syncNodeData({ maxRetries: newValue })
                  }}
                  min={0}
                  max={10}
                  className="h-8 text-xs"
                />
              </div>
              <div>
                <Label htmlFor={`retry-delay-${id}`} className="text-xs font-medium">
                  Retry Delay (ms)
                </Label>                <Input
                  id={`retry-delay-${id}`}
                  type="number"
                  value={retryDelay}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value) || 1000
                    setRetryDelay(newValue)
                    syncNodeData({ retryDelay: newValue })
                  }}
                  min={100}
                  max={60000}
                  className="h-8 text-xs"
                />
              </div>
              <div>
                <Label htmlFor={`timeout-${id}`} className="text-xs font-medium">
                  Timeout (ms)
                </Label>                <Input
                  id={`timeout-${id}`}
                  type="number"
                  value={timeout}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value) || 0
                    setTimeout(newValue)
                    syncNodeData({ timeout: newValue })
                  }}
                  min={1000}
                  className="h-8 text-xs"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Execution Controls */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                onClick={executeWorkflow}
                disabled={isExecuting || !workflowDefinition}
                size="sm"
                className="flex-1 h-8 text-xs"
              >
                <Play className="h-3 w-3 mr-1" />
                Execute
              </Button>
              {isExecuting && (
                <Button
                  onClick={stopWorkflow}
                  variant="destructive"
                  size="sm"
                  className="h-8 text-xs"
                >
                  <Square className="h-3 w-3" />
                </Button>
              )}
              <Button
                onClick={resetWorkflow}
                variant="outline"
                size="sm"
                className="h-8 text-xs"
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>

            {/* Overall Progress */}
            {(isExecuting || overallProgress > 0) && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium">Overall Progress</span>
                  <span className="text-xs text-gray-500">{Math.round(overallProgress)}%</span>
                </div>
                <Progress value={overallProgress} className="h-2" />
              </div>
            )}

            {/* Execution Status */}
            {executionId && (
              <div className="text-xs text-gray-500">
                Execution ID: {executionId}
              </div>
            )}
          </div>

          {/* Workflow Steps */}
          {workflowSteps.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label className="text-xs font-medium">Workflow Steps</Label>
                <div className="space-y-1">
                  {workflowSteps.map((step, index) => (
                    <div
                      key={step.id}
                      className={`flex items-center gap-2 p-2 rounded border ${
                        index === currentStep ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {getStepIcon(step.status)}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{step.name}</div>
                        <div className="text-xs text-gray-500">{step.type}</div>
                      </div>
                      <div className="text-xs">
                        <Badge
                          variant={
                            step.status === 'completed' ? 'default' :
                            step.status === 'failed' ? 'destructive' :
                            step.status === 'running' ? 'secondary' : 'outline'
                          }
                        >
                          {step.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Execution Result */}
          {executionResult && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label className="text-xs font-medium">Execution Result</Label>
                <div className="text-xs p-2 bg-gray-50 rounded border">
                  {executionResult.success ? (
                    <span className="text-green-600">✓ Completed successfully</span>
                  ) : (
                    <span className="text-red-600">✗ Execution failed</span>
                  )}
                </div>
              </div>
            </>
          )}        </CardContent>
      </Card>
          </div>
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        id="output"
        className="w-3 h-3 bg-purple-400 border-2 border-white"
        isConnectable={true}
      />
    </NodeBaseContainer>
  )
}
